import React, { useEffect, useState } from 'react';
import { IoArrowBack } from 'react-icons/io5';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { FaEdit } from 'react-icons/fa';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as apicall from '../../components/Apicall';
import Skeleton from '../../components/SekeltonLoder';
import Loader from '../../components/Loader';
import { getStatusBadge } from './MarketingCampaignList';

const MarketingCampaignDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const campaignId = id || location.state?.campaignId;
  const navState = location.state || {};

  const [campaign, setCampaign] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'delete' | 'send' | 'cancel'
  const [busy, setBusy] = useState(false);

  const fetchDetails = async () => {
    try {
      const res = await apicall.get(`campaign/${campaignId}`);
      if (res.success) {
        setCampaign(res.data);
        setSummary(res.data.summary);
      } else {
        toast.error('Failed to load campaign details.');
      }
    } catch (e) {
      toast.error(e.message.replace('Error:', ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!campaignId) {
      navigate('/marketing-campaigns');
      return;
    }
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const navigateBack = () => navigate('/marketing-campaigns', { state: navState });

  const runAction = async () => {
    setBusy(true);
    try {
      if (modal === 'delete') {
        const res = await apicall.get(`delete_campaign/${campaignId}`);
        if (res.success) { toast.success('Campaign deleted'); navigateBack(); }
        else toast.error(res.message);
      } else if (modal === 'cancel') {
        await apicall.get(`campaign/cancel/${campaignId}`);
        toast.success('Campaign cancelled');
        await fetchDetails();
      } else if (modal === 'send') {
        const res = await apicall.post({ campaign_id: campaignId }, 'campaign/send_now');
        toast.success(`Sent ${res.data.sent}, failed ${res.data.failed}, skipped ${res.data.skipped}`);
        await fetchDetails();
      }
    } catch (e) {
      toast.error(e.message.replace('Error:', ''));
    } finally {
      setBusy(false);
      setModal(null);
    }
  };

  const handleDuplicate = () => {
    // Open the create form prefilled is out of scope; navigate to a fresh form.
    navigate('/marketing-campaign/0', { state: { duplicateFrom: campaign } });
  };

  const formatScheduledAt = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${String(d.getUTCDate()).padStart(2,'0')}-${months[d.getUTCMonth()]}-${d.getUTCFullYear()} ${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
  };

  const detail = (label, value) => (
    <div className="col-4 mb-3">
      <p className="label mb-1">{label}</p>
      {loading ? <Skeleton /> : <p className="content mb-1">{value || 'N/A'}</p>}
    </div>
  );

  const stat = (label, value, color) => (
    <div className="col text-center p-2">
      <div style={{ fontSize: 26, fontWeight: 600, color: color || '#333' }}>{value ?? 0}</div>
      <div className="label">{label}</div>
    </div>
  );

  const canSend = campaign && ['DRAFT', 'SCHEDULED', 'QUEUED'].includes(campaign.status);
  const canCancel = canSend;

  return (
    <div className="container my-4 p-3 shadow rounded-3">
      <div className="mb-3 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <IoArrowBack size={24} onClick={navigateBack} className="me-2" style={{ cursor: 'pointer' }} />
          <h1 className="sub-heading mb-0">Campaign Details</h1>
        </div>
        <div>{!loading && getStatusBadge(campaign?.status)}</div>
      </div>

      {/* Metadata */}
      <div className="row mb-3 Secondary-Variant">
        <div className="col-12 p-4">
          <div className="row g-3">
            {detail('Campaign Name', campaign?.name)}
            {detail('Channel', campaign?.channel)}
            {detail('Type', campaign?.campaign_type)}
            {detail('Country', campaign?.country_name)}
            {detail('Audience', campaign?.audience_type === 'ALL_CUSTOMERS' ? 'All Customers' : 'User List')}
            {detail('Send Mode', campaign?.send_mode === 'SCHEDULED' ? 'Scheduled' : 'Send Now')}
            {detail('Scheduled At', formatScheduledAt(campaign?.scheduled_at))}
            {detail('Timezone', campaign?.timezone || '—')}
            {detail('Mode', campaign?.is_live ? 'LIVE (real audience)' : 'TEST (allowlist only)')}
            {detail('Priority', campaign?.priority)}
            {detail('Created By', campaign?.created_by)}
            {detail('Title', campaign?.title)}
            {detail('Message Body', campaign?.body)}
            {!campaign?.is_live && detail('Test Recipients', campaign?.test_recipients)}
          </div>
        </div>
      </div>

      {/* Run status panel */}
      <h5 className="sub-heading mt-2">Run Status</h5>
      <div className="row mb-3 LightbackgroundColour rounded p-2">
        {stat('Audience', summary?.total_recipients, '#1967d2')}
        {stat('Sent', summary?.total_sent, '#2e7d32')}
        {stat('Failed', summary?.total_failed, '#d01c4c')}
        {stat('Skipped', summary?.total_skipped, '#b26a00')}
        {stat('Pending', summary?.total_pending, '#555')}
      </div>
      <p className="label">
        Last run: {summary?.last_run_status || '—'}
        {summary?.last_run_at ? ` • ${new Date(summary.last_run_at).toLocaleString()}` : ''}
      </p>

      {/* Actions */}
      <div className="d-flex flex-wrap gap-2 mt-4">
        <button className="btn Button primaryColorText btn-outline-secondary no-hover-effect" onClick={() => navigate(`/marketing-campaign/${campaignId}`, { state: navState })}>
          <FaEdit className="me-2" />Edit
        </button>
        <button className="btn Button primaryColorText btn-outline-secondary no-hover-effect" onClick={handleDuplicate}>
          Duplicate
        </button>
        {canSend && (
          <button className="btn Button primaryBackground text-white" onClick={() => setModal('send')}>
            Send Now
          </button>
        )}
        {canCancel && (
          <button className="btn Button btn-outline-warning" style={{ fontSize: '12px', lineHeight: '1.3', textAlign: 'center' }} onClick={() => setModal('cancel')}>
            Cancel Campaign
          </button>
        )}
        <button className="btn Button btn-outline-danger" onClick={() => setModal('delete')}>
          <RiDeleteBin5Fill className="me-2" />Delete
        </button>
      </div>

      <Modal show={!!modal} onHide={() => setModal(null)} centered>
        <div className="d-flex flex-column justify-content-center align-items-center p-1 m-3">
          <p className="text-center">
            {modal === 'delete' && 'Are you sure you want to delete this campaign?'}
            {modal === 'cancel' && 'Cancel this campaign? It will no longer be sendable.'}
            {modal === 'send' && (campaign?.is_live
              ? 'This is a LIVE campaign — it will send to the real resolved audience. Continue?'
              : 'Send this TEST campaign to the allowlist now?')}
          </p>
          <div className="d-flex justify-content-center align-items-center mb-3 mt-3">
            <button className="mx-1 btn Button primaryColorText btn-outline-secondary no-hover-effect" onClick={() => setModal(null)}>
              No
            </button>
            <button className="btn Button primaryBackground text-white" onClick={runAction} disabled={busy}>
              {busy ? <Loader style={{ height: '30', width: '40' }} /> : 'Yes, Proceed'}
            </button>
          </div>
        </div>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default MarketingCampaignDetails;
